import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const formatDate = (d) => (d ? new Date(d).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' }) : '');
const where = (s) => [s.suburb, s.town, s.province].filter((x, i, all) => x && all.indexOf(x) === i).join(', ');
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

const usageText = (u) => {
  const parts = [];
  if (u.listings) parts.push(plural(u.listings, 'listing'));
  if (u.users) parts.push(plural(u.users, 'profile'));
  if (u.wishlist) parts.push(plural(u.wishlist, 'wishlist entry').replace('entrys', 'entries'));
  if (u.shops) parts.push(plural(u.shops, 'school shop'));
  return parts.length ? parts.join(', ') : 'Not used yet';
};

/**
 * Schools users added in the app (ones not on the Department of Basic Education list).
 * Admins approve them, or delete duplicates and move their listings to the right school.
 */
const SchoolsPage = () => {
  const [status, setStatus] = useState('pending');
  const [schools, setSchools] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [deleting, setDeleting] = useState(null); // the school whose delete panel is open
  const [replaceWith, setReplaceWith] = useState(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/schools', { params: { status } });
      setSchools(r.data.schools);
      setPendingCount(r.data.pending_count);
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.error || 'Could not load schools' });
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  // Search all listed schools for a replacement that isn't among the lookalikes
  useEffect(() => {
    if (!deleting || query.trim().length < 2) { setResults([]); return undefined; }
    const timer = setTimeout(() => {
      api.get('/schools/search', { params: { q: query.trim() } })
        .then(r => setResults(r.data.schools.filter(s => s.emis !== deleting.emis).slice(0, 8)))
        .catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [query, deleting]);

  const openDelete = (school) => {
    setDeleting(school);
    setReplaceWith(school.similar[0] || null);
    setQuery('');
    setResults([]);
    setMessage(null);
  };

  const approve = async (school) => {
    setBusy(true);
    setMessage(null);
    try {
      await api.post(`/admin/schools/${school.emis}/approve`);
      setMessage({ type: 'success', text: `${school.name} approved.` });
      load();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.error || 'Could not approve the school' });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    const school = deleting;
    const moving = replaceWith && (school.usage.listings || school.usage.users || school.usage.wishlist);
    const confirmText = replaceWith
      ? `Delete "${school.name}" and move its ${usageText(school.usage).toLowerCase()} to "${replaceWith.name}"?`
      : `Delete "${school.name}"?${school.usage.listings ? ` Its ${plural(school.usage.listings, 'listing')} will keep the old school name.` : ''}`;
    if (!window.confirm(`${confirmText}\n\nThis can't be undone.`)) return;
    setBusy(true);
    setMessage(null);
    try {
      await api.delete(`/admin/schools/${school.emis}`, { params: replaceWith ? { replaceWith: replaceWith.emis } : {} });
      setMessage({ type: 'success', text: moving ? `${school.name} deleted; moved to ${replaceWith.name}.` : `${school.name} deleted.` });
      setDeleting(null);
      load();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.error || 'Could not delete the school' });
    } finally {
      setBusy(false);
    }
  };

  const card = { backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' };
  const button = (bg) => ({ width: 'auto', padding: '6px 12px', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: 600, color: 'white', cursor: busy ? 'wait' : 'pointer', backgroundColor: bg });
  const pill = (selected) => ({ width: 'auto', padding: '8px 14px', borderRadius: '20px', cursor: 'pointer', fontSize: '13px', border: `2px solid ${selected ? '#00aacc' : '#ddd'}`, background: selected ? '#e6f7fb' : 'white', color: '#2c3e50' });

  const option = (s) => (
    <label key={s.emis} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', padding: '6px 0', cursor: 'pointer' }}>
      <input type="radio" name="replace" checked={replaceWith?.emis === s.emis} onChange={() => setReplaceWith(s)} style={{ width: 'auto', marginTop: '3px' }} />
      <span><strong>{s.name}</strong> <span style={{ color: '#777' }}>· {where(s)}</span></span>
    </label>
  );

  return (
    <div>
      <h1 style={{ marginBottom: '4px' }}>Listed schools</h1>
      <p style={{ marginTop: 0, color: '#666' }}>
        Schools users added because they weren't on the Department of Basic Education list. They can be found straight away; approve the real ones, and delete duplicates or misspellings, moving their listings to the right school.
      </p>

      <div style={{ display: 'flex', gap: '8px', margin: '12px 0' }}>
        <button type="button" style={pill(status === 'pending')} onClick={() => { setStatus('pending'); setDeleting(null); }}>
          <strong>Awaiting approval</strong> · {pendingCount}
        </button>
        <button type="button" style={pill(status === 'approved')} onClick={() => { setStatus('approved'); setDeleting(null); }}>
          <strong>Approved</strong>
        </button>
      </div>

      {message && <p style={{ color: message.type === 'error' ? '#c0392b' : '#27ae60', fontSize: '14px' }}>{message.text}</p>}

      <div style={{ ...card, overflowX: 'auto' }}>
        {loading ? (
          <p style={{ padding: '16px', margin: 0, color: '#7f8c8d' }}>Loading…</p>
        ) : schools.length === 0 ? (
          <p style={{ padding: '16px', margin: 0, color: '#7f8c8d' }}>{status === 'pending' ? 'No schools awaiting approval.' : 'No approved schools yet.'}</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ textAlign: 'left', background: '#f8f9fa' }}>
                {['School', 'Added', 'Used by', 'Looks like', ''].map(h => <th key={h} style={{ padding: '10px 12px' }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {schools.map(s => (
                <React.Fragment key={s.emis}>
                  <tr style={{ borderTop: '1px solid #eee', verticalAlign: 'top' }}>
                    <td style={{ padding: '10px 12px' }}><strong>{s.name}</strong><div style={{ color: '#777' }}>{where(s)}</div></td>
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                      {formatDate(s.added_at) || '—'}
                      {s.added_by && <div style={{ fontSize: '12px', color: '#999' }}>{s.added_by.name || s.added_by.email}<br />{s.added_by.email}</div>}
                    </td>
                    <td style={{ padding: '10px 12px' }}>{usageText(s.usage)}</td>
                    <td style={{ padding: '10px 12px' }}>
                      {s.similar.length === 0 ? <span style={{ color: '#999' }}>—</span> : s.similar.map(o => (
                        <div key={o.emis} style={{ color: '#c0392b' }}>{o.name} <span style={{ color: '#999' }}>· {where(o)}</span></div>
                      ))}
                    </td>
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {s.status !== 'approved' && <button type="button" disabled={busy} style={{ ...button('#27ae60'), marginRight: '6px' }} onClick={() => approve(s)}>Approve</button>}
                      <button type="button" disabled={busy} style={button('#c0392b')} onClick={() => (deleting?.emis === s.emis ? setDeleting(null) : openDelete(s))}>Delete</button>
                    </td>
                  </tr>
                  {deleting?.emis === s.emis && (
                    <tr style={{ background: '#fdf2f1' }}>
                      <td colSpan={5} style={{ padding: '12px 16px' }}>
                        <strong>Delete {s.name}</strong>
                        {s.usage.shops > 0 ? (
                          <p style={{ color: '#c0392b', margin: '8px 0' }}>A school shop uses this school, so it can't be deleted.</p>
                        ) : (
                          <>
                            <p style={{ margin: '6px 0', color: '#555' }}>
                              If it's a duplicate, move its {usageText(s.usage).toLowerCase()} to the right school:
                            </p>
                            {s.similar.map(option)}
                            <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Or search for another school"
                              style={{ width: '100%', maxWidth: '420px', padding: '8px', border: '1px solid #ddd', borderRadius: '6px', margin: '6px 0', boxSizing: 'border-box' }} />
                            {results.filter(r => !s.similar.some(o => o.emis === r.emis)).map(option)}
                            {replaceWith && !s.similar.some(o => o.emis === replaceWith.emis) && !results.some(r => r.emis === replaceWith.emis) && option(replaceWith)}
                            <label style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '6px 0', cursor: 'pointer' }}>
                              <input type="radio" name="replace" checked={!replaceWith} onChange={() => setReplaceWith(null)} style={{ width: 'auto' }} />
                              <span>Don't move anything — just delete it</span>
                            </label>
                            <div style={{ marginTop: '8px' }}>
                              <button type="button" disabled={busy} style={{ ...button('#c0392b'), marginRight: '6px' }} onClick={remove}>
                                {replaceWith ? `Delete and move to ${replaceWith.name}` : 'Delete'}
                              </button>
                              <button type="button" style={button('#95a5a6')} onClick={() => setDeleting(null)}>Cancel</button>
                            </div>
                          </>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default SchoolsPage;
